/**
   * @author Jimmy Messmer
   * @file Contains utility classes that we use for Pancreatlas.
   */

/**
 * Class for our data structure holding filter information.
 */
export class FilterTree {
  /**
   * Create a root node, count of active filters, and operation stack. Also set the default marker logic
   */
  constructor () {
    this.root = new FilterNode('root', 'filters')
    this.activeFilters = 0
    this.operationStack = []
    this.markerLogic = 'OR'
  }

  // buildFromJSON (serializedTree) {
  //   // let tree = JSON.parse(serializedTree)
  //   //console.log(tree)
  // }

  /**
   * Add a hierarchy of nodes to the filter tree. Used for subdividing a filter set into several groups.
   * For example, dividing age between neonatal, infant, etc.
   * @param {string} n List of parent nodes, separated by '-'
   */
  addSet (n, sortMethod, filterMethod, defaultHidden) {
    let levels = n.split('-')
    let curr = levels.shift()
    let node = this.search(curr)
    if (node === undefined) {
      node = new FilterNode('node', curr, sortMethod, filterMethod, defaultHidden)
      // Add a category property on the node
      node.category = curr;
      this.root.addNode(node)
    }
    while (levels.length > 0) {
      curr = levels.shift()
      let nextNode = new FilterNode('node', curr, sortMethod, filterMethod, defaultHidden)
      // Add a category property on the node
      nextNode.category = curr
      node.addNode(nextNode)
      node = nextNode
    }
    return node
  }

  /**
   * Adds a new node to the tree. First, it checks to see if the parent node exists. If it does, then it adds
   * the new node as a child of the parent. If not, it adds the parent as a child of root, then the node as a
   * child of the new parent
   * @param {string} n Name of the new node we are adding
   * @param {string} parent Name of the parent node for the new one
   * @param {string} filterMethod How we should display this filter in the UI (as a slider, checkbox, etc)
   */
  addNode (n, parent, sortMethod, filterMethod = undefined, defaultHidden = false) {
    let parentChain = parent.split('-')
    let parentNode = this.search(parentChain[parentChain.length - 1])
    if (parentNode === undefined) {
      parentNode = this.addSet(parent, sortMethod, filterMethod, defaultHidden)
    }
    if (parentNode !== undefined) {
      let leaf = new FilterNode('leaf', n, sortMethod, filterMethod)
      leaf.category = parentChain.pop()
      leaf.disabled = false
      parentNode.addNode(leaf)
    }
  }

  /**
   * Adds image to a given leaf node
   * @param {string} n Node to add image to
   * @param {int} i ID of image
   */
  addImg (n, i) {
    let node = this.search(n)
    if (node !== undefined) {
      node.addImage(i)
    }
  }

  /**
   * Basic breadth-first search to find a given node. Returns undefined if not found.
   * @param {string} n Name of node we are looking for
   */
  search (n) {
    let searchQueue = []
    searchQueue.unshift(this.root)
    while (searchQueue.length > 0) {
      let current = searchQueue.pop()
      if ((undefined === current.category && current.value === n) || 
          (n.indexOf(current.category) >= 0 && current.value === n.split('|').pop())){
        return current
      }
      for (let child of current.children) {
        searchQueue.unshift(child)
      }
    }
    return undefined
  }

  /**
   * Reset our tree so that all filters are either active or unactive.
   * @param {boolean} val Reset each node in the tree to this value
   */
  resetTo (val) {
    let q = []
    q.unshift(this.root)

    while (q.length > 0) {
      let current = q.pop()
      current.active = val
      current.disabled = false
      for (let child of current.children) {
        q.unshift(child)
      }
    }
    this.activeFilters = 0
  }

  /**
   * Pops the top filter in our operation stack, then activate it.
   */
  undo () {
    let filter = this.operationStack.pop()
    this.activateFilter(filter)
  }

  /**
   * Toggle specified node--flip true to false and vice versa.
   * @param {string} n Node to activate
   */
  activateFilter (n) {
    if (this.activeFilters <= 0) {
      this.resetTo(false)
    }
    let searchQueue = []
    searchQueue.unshift(this.root)
    while (searchQueue.length > 0) {
      let current = searchQueue.pop()
      // Check if the current category and value match the concatenated category-value of n
      if (n.indexOf(current.category) >= 0 && current.value === n.split('|').pop()) {
        let active = current.activate()
        if (active) {
          this.activeFilters++
          this.operationStack.push(n)
        } else {
          this.activeFilters--
          this.operationStack.pop(n)
        }
        if (this.activeFilters <= 0) {
          this.resetTo(false)
        }
        return true
      }
      for (let child of current.children) {
        searchQueue.unshift(child)
      }
    }
    return false
  }

  /**
   * Gets all nodes (filter sets) for this tree
   */

  generateAllNodes () {
    let q = []
    let nodes = []
    for (let child of this.root.children) {
      q.unshift(child)
    }
    while (q.length > 0) {
      let curr = q.pop()
      if (curr.type === 'node') {
        nodes.push(curr.value)
        for (let child of curr.children) {
          q.unshift(child)
        }
      }
    }
    return nodes
  }

  /**
   * Generate a list of all the active images in the tree
   */
  generateActiveImages () {
    if (this.activeFilters <= 0) {
      return this.generateAllImages()
    }
    return this.root.getActives(this.markerLogic || 'OR')
  }

  /**
   * Toggle the filter item disable flag based on matching images
   */
   toggleDisableFlag(matches) {
    let searchQueue = []

    searchQueue.unshift(this.root)
    while(searchQueue.length > 0) {
      let current = searchQueue.pop()
      if (current.type === 'leaf' && current.filterMethod === 'checkbox' && current.images.length > 0 && this.allowMultipleSelection(current.category)) {
        let ids = current.images.join('-')
        let found = false
        for(let m in matches) {
          if (ids.indexOf(matches[m]) >= 0) {
            found = true
            current['initial_disabled_state'] = !found
            break;
          }
        }

        if (!current.disabled && current['initial_disabled_state'] === undefined) {
          current.disabled = !found;
        } 
      }
      for (let child of current.children) {
        searchQueue.unshift(child)
      }
    }
  }
  clearDisabledState() {
    let searchQueue = []

    searchQueue.unshift(this.root)
    while(searchQueue.length > 0) {
      let current = searchQueue.pop()
      if (current.type === 'leaf' && current.filterMethod === 'checkbox' && current.images.length > 0) {
        if (this.allowMultipleSelection(current.category)) {
          delete current['initial_disabled_state']
          current.disabled = false    
          
        } 
      }
      for (let child of current.children) {
        searchQueue.unshift(child)
      }
    }
  }
  allowMultipleSelection(category) {
    return [
      //'DATASET', 
      // 'DISEASE STATUS', 
      // 'DISEASE DURATION', 
      // 'AGE',
      // 'SEX',
      // 'PROGRAM ID',
      // 'MARKER',
      // 'PANCREAS REGION',
      // 'MODALITY',
      // 'AUTOLYSIS',
      // 'INSULITIS',
      // 'PANCREATITIS',
      // 'RESIDUAL ICIS',
      // 'PROCESSING TYPE'
      ].indexOf(category) < 0
  }

  getNonActiveFilters(tree) {
    if (!tree.children) {
      return tree;
    }

    return tree.children
      .map(this.getNonActiveFilters)
      .reduce((acc, item) =>  {
        if (!item.active) {
          acc.concat(item)
        }
        return null
      }, []);
  }

  /**
   * Generate a list of all images in the tree
   */
  generateAllImages () {
    let q = []
    let images = []
    for (let child of this.root.children) {
      q.unshift(child)
    }
    while (q.length > 0) {
      let curr = q.pop()
      if (curr.type !== 'leaf') {
        for (let child of curr.children) {
          q.unshift(child)
        }
      } else {
        /* eslint-disable no-loop-func */
        let newImgs = curr.images.filter(img => !images.includes(img))
        images = images.concat(newImgs)
        /* eslint-enable no-loop-func */
      }
    }
    return images
  }

  /**
   * Sort all images in the tree based on certain criteria
   * @param {string} f Sort criteria (age, disease duration, etc)
   * @param {function} sortFn Custom sorting function
   */
  sortImages (f, sortFn = ((a, b) => a.value > b.value ? 1 : -1)) {
    let filterNode = this.search(f)
    let images = []
    if (filterNode !== undefined) {
      // let sortedKeys = filterNode.children.map(child => child.value).sort() // Object.keys(filterNode).sort()
      filterNode.children.sort(sortFn)
      let q = Object.assign([], filterNode.children)
      while (q.length > 0) {
        let curr = q.shift()
        curr.children.sort(sortFn)
        for (let child of curr.children) {
          q.push(Object.assign({}, child))
        }
        for (let img of curr.images) {
          images.push(img)
        }
      }
    }
    return images
  }

  /**
   * Recursively generate a JSON representation of the filter tree.
   * @param {FilterNode} node current filter node
   */
  generateJSON (node) {
    if (node.type === 'leaf') {
      let tmpLeaf = {}
      tmpLeaf['name'] = node.value
      tmpLeaf['active'] = node.active
      tmpLeaf['type'] = 'leaf'
      tmpLeaf['disabled'] = node.disabled
      tmpLeaf['sortMethod'] = node.sortMethod
      return tmpLeaf
    } else {
      let tmp = {}
      tmp['name'] = node.value
      tmp['filterMethod'] = node.filterMethod
      tmp['type'] = 'node'
      tmp['sortMethod'] = node.sortMethod
      tmp['defaultHidden'] = node.defaultHidden
      tmp['children'] = node.children.map(child => this.generateJSON(child))
      return tmp
    }
  }
}

/**
 * Class for an individual node in the filterTree
 */
class FilterNode {
  /**
   *
   * @param {string} type Type of node--root, filter set, or leaf
   * @param {string} value Value of node (name of filter or set, etc)
   * @param {*} filterMethod Type of filter to display in UI
   */
  constructor (type, value, sortMethod, filterMethod = undefined, defaultHidden = false) {
    this.type = type
    this.value = value
    this.filterMethod = filterMethod
    this.sortMethod = sortMethod
    this.children = []
    this.images = []
    this.defaultHidden = defaultHidden
  }

  /**
   * Add node to list of children of this node
   * @param {FilterNode} n Node to add
   */
  addNode (n) {
    this.children.push(n)
  }

  /**
   * Add image to list of images for a given filter
   * @param {int} i Id of image to add
   */
  addImage (i) {
    this.images.push(i)
  }

  /**
   * Toggle active letiable
   */
  activate () {
    this.active = !this.active
    return this.active
  }

  /**
   * Toggle disable flag
   */
  toggleDisabled () {
    this.disabled = !this.disabled
    return this.disabled
  }

  /**
   * Get all active images that are a descendent of this node
   */
  getActives(markerLogic = 'OR') {
    if (this.type === 'leaf') {
      if (this.active) {
        return this.images
      } else {
        return []
      }
    } else {
      if (this.type === 'root') {
        let matches = this.children[0].getActives(markerLogic)
        for (let child of this.children.slice(1)) {
          let childMatches = child.getActives(markerLogic)
          if (childMatches.length > 0) {
            if (matches.length > 0) {
              matches = matches.filter(val => childMatches.includes(val))
            } else {
              matches = matches.concat(childMatches)
            }
          }
        }
        return matches
      } 
      else if (this.value === 'MARKER') {
        if (markerLogic === 'OR') {
          let markerMatches = []
          for (let child of this.children) {
            let tmp = child.getActives(markerLogic)
            markerMatches = markerMatches.concat(tmp.filter(val => markerMatches.includes(val) === false))
          }
          return markerMatches
        } else if (markerLogic === 'AND') {
          let activeChildren = []
          for (let child of this.children) {
            let tmp = child.getActives(markerLogic)
            if (tmp.length > 0) {
              activeChildren.push(tmp)
            }
          }
          if (activeChildren.length === 0) return []
          let matches = activeChildren[0]
          for (let i = 1; i < activeChildren.length; i++) {
            matches = matches.filter(val => activeChildren[i].includes(val))
          }
          return matches
        }
      } 
      else {
        let nodeMatches = []
        for (let child of this.children) {
          let tmp = child.getActives(markerLogic)
          nodeMatches = nodeMatches.concat(tmp.filter(val => nodeMatches.includes(val) === false))
        }
        return nodeMatches
      }
    }
  }
  
  
  

  

}


export function compareAges (age1, age2) {
  let ageGroupRe = /^(NEONATAL)|(CHILDHOOD)|(INFANCY)|(ADULT)$/i
  
    if (ageGroupRe.test(age1) && ageGroupRe.test(age2)) {
      const ageOrder = {
        NEONATAL: 0,
        INFANCY: 1,
        CHILDHOOD: 2,
        ADULT: 3,
      };
  
      return ageOrder[age1.toUpperCase()] - ageOrder[age2.toUpperCase()];
    }
  
    
    const convertAge = (age) => {
      if (age === '<0.1y') return '0.1y';
      if (age === '.3y') return '0.3y';
      return age;
    };
  
    age1 = convertAge(age1);
    age2 = convertAge(age2);
  
    const ageRe = /^(G)?(\d+\.?\d*)(d|w|mo|y)(\+\d+d|w|mo|y)?$/;
    const a = ageRe.exec(age1);
    const b = ageRe.exec(age2);
  
    
    if (!a || !b) {
      return 0;
    }
  
    const timeUnitOrder = { d: 0, w: 1, mo: 2, y: 3 };
  
    
    const aUnit = timeUnitOrder[a[3]] || -1;
    const bUnit = timeUnitOrder[b[3]] || -1;
    if (aUnit !== bUnit) return aUnit - bUnit;
  
    // Compare numerical values
    const aValue = parseFloat(a[2]) || 0;
    const bValue = parseFloat(b[2]) || 0;
    if (aValue !== bValue) return aValue - bValue;
  
    return 0; 
  }

export function isArray (obj) {
  return Object.prototype.toString.call(obj) === '[object Array]'
}

export function extractFilters (tagObj) {
  if (isArray(tagObj)) {
    return tagObj
  } else {
    let objectQueue = []
    let filters = []
    for (let key of Object.keys(tagObj)) {
      objectQueue.push(tagObj[key])
    }
    while (objectQueue.length > 0) {
      let curr = objectQueue.shift()
      if (isArray(curr)) {
        filters = filters.concat(curr)
      } else {
        for (let k of Object.keys(curr)) {
          objectQueue.push(curr[k])
        }
      }
    }
    return filters
  }
}
